import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Clock,
  ShieldCheck,
  Check,
  X,
  SlidersHorizontal,
  Timer,
} from "lucide-react";
import { useUserRegion } from "@/hooks/useUserRegion";
import {
  CONSENT_STORAGE_KEY,
  grantedConsent,
  deniedConsent,
  type GoogleConsentState,
  updateGoogleConsent,
} from "@/lib/googleTag";

const BANNER_DISMISSED_KEY = "bloxida-consent-banner-dismissed";
const AUTO_APPROVE_SECONDS = 3;

export function GoogleConsentBanner() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  const { regionInfo, currentTime, requestPreciseLocation } = useUserRegion();

  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(BANNER_DISMISSED_KEY) === "true";
  });

  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [adsEnabled, setAdsEnabled] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState<number>(AUTO_APPROVE_SECONDS);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(BANNER_DISMISSED_KEY, "true");
    setDismissed(true);
  }, []);

  const handleAutoApprove = useCallback(() => {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(grantedConsent));
    updateGoogleConsent(grantedConsent);
    window.dispatchEvent(new CustomEvent("bloxida:consent-changed"));
    handleDismiss();
  }, [handleDismiss]);

  // 3-second auto-approval countdown unless preferences modal is open
  useEffect(() => {
    if (isAdmin || dismissed || showPreferences) return;

    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleAutoApprove();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isAdmin, dismissed, showPreferences, handleAutoApprove]);

  if (isAdmin || dismissed) return null;

  const handleSavePreferences = () => {
    const customState: GoogleConsentState = {
      analytics_storage: analyticsEnabled ? "granted" : "denied",
      ad_storage: adsEnabled ? "granted" : "denied",
      ad_user_data: adsEnabled ? "granted" : "denied",
      ad_personalization: adsEnabled ? "granted" : "denied",
    };
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(customState));
    updateGoogleConsent(customState);
    window.dispatchEvent(new CustomEvent("bloxida:consent-changed"));
    handleDismiss();
  };

  const handleAllowLocation = async () => {
    await requestPreciseLocation();
    handleDismiss();
  };

  const handleRejectAll = () => {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(deniedConsent));
    updateGoogleConsent(deniedConsent);
    window.dispatchEvent(new CustomEvent("bloxida:consent-changed"));
    handleDismiss();
  };

  return (
    <aside
      className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-4xl rounded-2xl border border-border/80 bg-background/95 p-4 backdrop-blur-xl shadow-2xl transition-all animate-in fade-in slide-in-from-bottom-4 duration-300"
      role="region"
      aria-label="Regional and analytics preferences"
    >
      <div className="flex flex-col gap-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-foreground">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
              <MapPin className="h-3.5 w-3.5" />
              <span>{regionInfo.regionName}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-muted-foreground border border-border">
              <Clock className="h-3.5 w-3.5 text-primary" />
              <span>Local Time: {currentTime}</span>
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Analytics Active</span>
            </span>
            {!showPreferences && secondsLeft > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] text-primary/80 font-medium bg-primary/5 px-2 py-0.5 rounded-full border border-primary/15">
                <Timer className="h-3 w-3 animate-spin" />
                <span>Auto-approving in {secondsLeft}s</span>
              </span>
            )}
          </div>

          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground p-1 rounded-lg hover:bg-muted transition-colors"
            aria-label="Close banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!showPreferences ? (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-1 border-t border-border/50">
            <p className="text-xs text-muted-foreground leading-relaxed">
              BloXida auto-calibrates content, timezone metrics, and regional news based on your location. Full consent and location detection auto-approve in {secondsLeft}s unless customized.
            </p>

            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs font-semibold"
                onClick={() => setShowPreferences(true)}
              >
                <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
                Customize
              </Button>

              {!regionInfo.isPrecise && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs font-semibold border-primary/40 text-primary hover:bg-primary/10"
                  onClick={handleAllowLocation}
                >
                  <MapPin className="h-3.5 w-3.5 mr-1.5" />
                  Allow GPS Location
                </Button>
              )}

              <Button
                size="sm"
                className="h-8 text-xs font-bold"
                onClick={handleAutoApprove}
              >
                <Check className="h-3.5 w-3.5 mr-1.5" />
                Accept Now ({secondsLeft}s)
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 pt-2 border-t border-border/50 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="flex items-center justify-between p-2.5 rounded-lg border border-border bg-muted/30 cursor-pointer">
                <span className="font-semibold text-foreground">Analytics & Performance</span>
                <input
                  type="checkbox"
                  checked={analyticsEnabled}
                  onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                />
              </label>

              <label className="flex items-center justify-between p-2.5 rounded-lg border border-border bg-muted/30 cursor-pointer">
                <span className="font-semibold text-foreground">Content Personalization</span>
                <input
                  type="checkbox"
                  checked={adsEnabled}
                  onChange={(e) => setAdsEnabled(e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary h-4 w-4"
                />
              </label>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={handleRejectAll}
              >
                Reject All
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => setShowPreferences(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs font-bold"
                onClick={handleSavePreferences}
              >
                Save Preferences
              </Button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
